import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"

import { FEATURED_CONTRIBUTORS, TRACK_CONTRIBUTORS } from "../_fixtures"

import { HouseRule, ManualSection, Plate } from "./primitives"

// Where the energy goes. For a youth mental-health audience, vibrancy
// should read as alive and hopeful. It comes from a second warm accent,
// from visible people, and from confident type, never from more hue per
// square inch.
export function EnergySection() {
  const quoteByline = [...FEATURED_CONTRIBUTORS, ...TRACK_CONTRIBUTORS].slice(
    0,
    3,
  )

  return (
    <ManualSection
      id="energy"
      title="Where the energy goes"
      lede="Vibrancy here comes from a second warm accent, from visible people, and from confident type. It should read as alive and hopeful. What it never comes from is cramming more hue into every square inch. Keep the budget small within a single view; across a whole journey it adds up."
    >
      <h3 className="text-primary text-xl font-semibold">
        Activate the dormant warm accents
      </h3>
      <Plate
        title="Four warm notes, one at a time"
        note="Red Pencil and Stet Green now sit in code beside the highlighter and navy. All four accents are warm; the retired mint stays retired."
      >
        <div className="flex flex-wrap gap-3">
          <span className="bg-surface-action text-on-action inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold">
            <span className="size-2 rounded-full bg-current" />
            Highlighter · the action
          </span>
          <span className="bg-batyr-red-pencil inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white">
            <span className="size-2 rounded-full bg-current" />
            Red Pencil · contributor energy
          </span>
          <span className="bg-surface-affirm text-on-affirm inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold">
            <span className="size-2 rounded-full bg-current" />
            Stet Green · affirmation
          </span>
          <span className="bg-surface-strong text-primary_on-brand inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold">
            <span className="size-2 rounded-full bg-current" />
            Navy · weight & drama
          </span>
        </div>
      </Plate>
      <HouseRule name="One moment at a time: rotate it across views">
        The Highlighter Rule isn&rsquo;t &ldquo;one color forever,&rdquo;
        it&rsquo;s &ldquo;one moment at a time.&rdquo; Yellow lands on this
        screen, a navy band on the next, a clay-red flourish after the one after
        that. Stay disciplined on each view and the whole journey reads as
        vibrant. Red Pencil lives in marginalia and byline presence, never as an
        edge stripe.
      </HouseRule>

      <h3 className="text-primary text-xl font-semibold">
        Let people be the energy
      </h3>
      <Plate
        title="The contributor pull-quote"
        note="Emphasis comes from type and shadow-xs. Varied avatar colors and a contributor's own words carry more energy than any decoration, and they encode who's present."
      >
        <blockquote className="font-display text-primary max-w-prose text-xl leading-snug font-medium text-pretty">
          &ldquo;I made this for the version of me that couldn&rsquo;t get out
          of bed last winter. Someone else&rsquo;s version of that is on here
          too.&rdquo;
        </blockquote>
        <div className="flex items-center gap-3">
          <div className="flex items-center -space-x-2">
            {quoteByline.map((c) => (
              <Avatar
                key={c.id}
                size="sm"
                name={c.display_name}
                avatarUrl={c.user?.avatar_url}
                className="ring-2 ring-white"
              />
            ))}
          </div>
          <p className="text-tertiary text-sm">
            <span className="text-primary font-semibold">Priya</span> started
            this brief ·{" "}
            <span className="text-primary font-semibold">June</span> and{" "}
            <span className="text-primary font-semibold">Theo</span> added to it
          </p>
        </div>
      </Plate>

      <h3 className="text-primary text-xl font-semibold">
        A confident editorial moment
      </h3>
      <div className="bg-brand-section flex flex-col gap-6 rounded-2xl p-8 lg:p-10">
        <div className="flex max-w-2xl flex-col gap-4">
          <p className="text-on-action text-xs font-semibold tracking-[0.08em] uppercase">
            <span className="bg-surface-action rounded-xs px-1.5 py-0.5">
              Loud about the quiet stuff
            </span>
          </p>
          <p className="font-display text-primary_on-brand text-3xl font-semibold text-balance lg:text-4xl">
            Start a brief for someone having a hard week.
          </p>
          <p className="text-secondary_on-brand text-pretty">
            The navy weight and the voice carry this block. One yellow action
            lands the moment on a dark surface.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button color="primary" size="lg">
            Start a brief
          </Button>
          <Button
            color="secondary"
            size="lg"
            className="text-primary_on-brand border-white/30 bg-transparent shadow-none hover:bg-white/10"
          >
            See examples
          </Button>
        </div>
      </div>
      <HouseRule name="Don't overdo it">
        No streaks, XP, confetti, or badges-as-rewards. For this audience, those
        read as manipulative. No rainbow gradients or hue spread, and no
        reviving the mint. Keep the energy warm and safe, and keep it hopeful.
      </HouseRule>
    </ManualSection>
  )
}
